import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { baseAuthApi, BasePermissionsResp, BaseTenantResp, BaseLoyaltySettingsResp, BaseBranch } from '../api/baseAuthApi';
import { BaseApiError, baseApi } from '../api/baseClient';
import { billingApi, OwnerAccount } from '../api/billingApi';
import { whatsappApi, type WhatsAppStatus } from '../api/whatsappApi';
import { User, Branch } from '../types/user';
import { useError } from './ErrorContext';
import { isPro as checkIsPro } from '../utils/planPermissions';

// ── Re-cableo del manager a lealify-base (reemplazo de Firebase Auth + Firestore) ──
// El CONTRATO de useAuth() es idéntico al de la versión Firebase (currentUser/login/
// logout/needsOnboarding + métodos de escritura) → los ~decenas de consumidores no
// cambian. La sesión vive en la cookie httpOnly de lealify-base.
//
// Diferidos a sus incrementos (no rompen el panel, se comportan en local mientras tanto):
//   register/onboarding → Inc2 ·  updateCustomMessage → Inc11 (mensajes) ·
//   branding completo (nombre/colores del tenant) → Inc3 (Mi Negocio).
// Prefs de UI sin equivalente en base (instructionsDismissed/howtosSeen) → localStorage.

interface AuthState {
    user: User | null;
    loading: boolean;
    error: string | null;
    needsOnboarding: boolean;
    /** Módulo cross "tours": marcadores visto-una-vez del operador (users.tours en base).
     *  null = sesión sin cargar o sin operador; {} = operador que no ha visto ninguno. */
    tours: Record<string, unknown> | null;
}

interface AuthContextType {
    currentUser: User | null;
    loading: boolean;
    error: string | null;
    needsOnboarding: boolean;
    register: (email: string, password: string, displayName: string, businessName: string, phoneNumber?: string, countryCode?: string, marketing?: { gaClientId?: string | null; gaSessionId?: string | null; utm?: Record<string, string> | null; landing?: Record<string, string> | null }) => Promise<void>;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    clearError: () => void;
    updateBusinessInfo: (businessId: string, data: Partial<Omit<User, 'uid' | 'email' | 'role' | 'subscription' | 'globalThemes'>>) => Promise<void>;
    updateBusinessTheme: (businessId: string, themeId: string) => Promise<void>;
    updateGlobalThemes: (businessTypeId: string, themeData: any) => Promise<void>;
    markOnboardingComplete: () => Promise<void>;
    markInstructionsDismissed: () => Promise<void>;
    markHowtoSeen: (sectionKey: string) => Promise<void>;
    registeredClients: number;
    instructionsDismissed: boolean;
    updateCustomMessage: (messageType: string, message: string) => Promise<void>;
    /** Módulo cross "tours" (coach-marks): { key: fecha } del operador; null sin sesión. */
    tours: Record<string, unknown> | null;
    /** Refresca en memoria campos del usuario que un interruptor ya guardó en el servidor (24-oct-2026: el folio
     *  pasaba a «No pedir» y el registro de visita lo seguía exigiendo hasta volver a iniciar sesión). */
    patchCurrentUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Analytics: etiqueta la sesión GA4 con negocio y rol (user properties) para poder
// segmentar el uso de pantallas por tenant/rol en los reportes. No-op si gtag no cargó
// (bots QA con navigator.webdriver, bloqueadores). Dimensiones en GA4: tenant_slug, base_role.
function tagAnalyticsUser(tenantSlug: string | null, baseRole: string) {
    try {
        const g = (window as any).gtag;
        if (typeof g === 'function' && !(window as any).__isBot) {
            g('set', 'user_properties', { tenant_slug: tenantSlug || '(sin-tenant)', base_role: baseRole });
        }
    } catch { /* analytics nunca rompe el panel */ }
}

// Prefs de UI locales (no las guarda base): clave por tenant para no mezclar negocios.
const prefsKey = (tenant: string) => `lf_uiprefs_${tenant}`;
function readPrefs(tenant: string): { instructionsDismissed?: boolean; howtosSeen?: Record<string, boolean> } {
    try { return JSON.parse(localStorage.getItem(prefsKey(tenant)) || '{}'); } catch { return {}; }
}
function writePrefs(tenant: string, p: object) {
    try { localStorage.setItem(prefsKey(tenant), JSON.stringify(p)); } catch { /* noop */ }
}

// Arma el objeto User del manager a partir de los endpoints de base.
function buildUser(
    perms: BasePermissionsResp,
    tenant: BaseTenantResp,
    loyalty: BaseLoyaltySettingsResp,
    branches: BaseBranch[],
    registeredClients: number,
    account: OwnerAccount | null = null,
    waStatus: WhatsAppStatus | null = null,
): User {
    const u = perms.user!;
    const s = loyalty.settings;
    const prefs = readPrefs(tenant.businessId);
    // base distingue owner/manager/staff (operadores del negocio). El manager solo
    // conoce 'admin' (plataforma) vs 'manager' (dueño): todo operador de negocio = 'manager'.
    const role: 'admin' | 'manager' = 'manager';
    // Rol real + módulos con nivel efectivo (módulo Equipo / gates por pantalla). Aditivo:
    // ningún consumidor previo los usa; `role` aplanado se conserva tal cual.
    const baseRole = u.role;
    const modules: User['modules'] = {};
    for (const m of perms.modules || []) {
        modules[m.key] = { level: (m.level as 'none' | 'view' | 'operate' | 'manage') || 'none', active: !!m.active };
    }
    // Suscripción REAL (Inc13 F13.6) de /billing/subscription; fallback al sintético
    // (loyalty.isPro) si el endpoint falló, para no dejar al panel sin plan.
    const subscription = account ? account.subscription : {
        plan: loyalty.isPro ? 'pro' : 'free',
        status: 'active',
        startDate: null,
        endDate: null,
        paymentMethod: null,
    };
    // maxBranches real de la suscripción; si no, el conteo de sucursales (comportamiento previo).
    const maxBranches = account ? Math.max(1, account.maxBranches) : Math.max(1, branches.length);
    const mappedBranches: Branch[] = branches.map((b) => ({
        id: String(b.id),
        name: b.name,
        isActive: b.is_active !== 0,
        cashbackPercentage: b.cashback_percentage != null ? Number(b.cashback_percentage) : null,
    } as Branch));
    return {
        uid: String(u.id),
        email: u.email,
        displayName: u.name || null,
        businessName: tenant.businessName || null,
        branches: mappedBranches,
        businessId: tenant.businessId || null,
        businessColors: tenant.colors || null,
        businessType: tenant.businessType || 'standard',
        pointsPerVisit: Number(s.points_per_visit ?? 1),
        timeBetweenVisits: Number(s.time_between_visits_min ?? 60),
        requestGender: !!s.request_gender,
        requestBirthday: !!s.request_birthday,
        requestEmail: !!s.request_email,
        allowPointsAccumulation: s.allow_points_accumulation !== 0,
        loyaltyMode: s.loyalty_mode || 'visits',
        cashbackPercentage: Number(s.cashback_percentage ?? 10),
        cashbackSpecialTag: s.cashback_special_tag ? String(s.cashback_special_tag) : null,
        cashbackSpecialPercentage: s.cashback_special_percentage != null && s.cashback_special_percentage !== ''
            ? Number(s.cashback_special_percentage) : null,
        specialDays: Array.isArray(s.special_days) ? s.special_days : [],   // días con premio extra (24-oct-2026)
        backdateVisits: !!(loyalty as any).backdateVisits,
        cardScanEnabled: Number(s.card_scan_enabled) === 1,   // tarjeta QR del cliente (23-oct-2026)
        ticketRef: ((loyalty as any).ticketRef as 'off' | 'optional' | 'required') || 'off',
        intlPhones: !!(loyalty as any).intlPhones,
        memberships: !!(loyalty as any).memberships,   // Membresías F1 (20-oct-2026): flag por negocio
        deliveryNavFirst: !!(loyalty as any).deliveryNavFirst,   // Repartos primero en el menú (28-oct-2026, Cenizas)
        birthdayPointsMode: s.birthday_points_mode || 'disabled',
        referralsEnabled: !!s.referrals_enabled,
        pointsPerReferral: Number(s.points_per_referral ?? 0),
        referralBonusForNewClient: Number(s.referral_bonus_new_client ?? 0),
        role,
        baseRole,
        modules,
        logoUrl: tenant.logoUrl || null,
        phoneNumber: (u as any).phone || null,   // teléfono del dueño (users.phone) → evita el prompt del dashboard
        countryCode: '+52',
        timeZone: tenant.timeZone || 'America/Mexico_City',
        vertical: tenant.vertical || 'lealtad',
        planReadOnly: tenant.planReadOnly === true,
        contactLimit: tenant.contactLimit || null,
        socialUrl: loyalty.social?.socialUrl || null,
        socialType: loyalty.social?.socialType || null,
        subscription,
        // Estado real del backend (tenants.onboarded). Solo un negocio recién creado por
        // signup (onboarded=false) dispara el wizard; los existentes vienen onboarded=true.
        // Si el campo falta (respuesta vieja en caché) → true, para no forzar onboarding.
        hasCompletedOnboarding: tenant.onboarded !== false,
        registeredClients,
        instructionsDismissed: prefs.instructionsDismissed || false,
        howtosSeen: prefs.howtosSeen || {},
        maxBranches,
        // Bloque C: estado real de WhatsApp del negocio desde /messages/whatsapp-status.
        whatsappEnabled: !!waStatus?.configured,                       // aprovisionado (sesión configurada)
        whatsappRequested: waStatus?.requested ? new Date() : null,    // solicitud "bajo solicitud" viva
        whatsappDisclaimer: waStatus?.disclaimer
            ? { accepted: !!waStatus.disclaimer.accepted, acceptedAt: waStatus.disclaimer.acceptedAt || null, version: waStatus.disclaimer.version }
            : null,
    } as User;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [authState, setAuthState] = useState<AuthState>({
        user: null,
        loading: true,
        error: null,
        needsOnboarding: false,
        tours: null,
    });

    const { addError } = useError();

    // Carga el contexto completo del operador desde base (tras login o al restaurar sesión).
    const loadContext = useCallback(async (): Promise<void> => {
        const perms = await baseAuthApi.permissions();
        if (perms.platform) {
            // Super_admin de plataforma (Carlos): NO tiene negocio propio. Armamos una sesión
            // admin mínima (role='admin') que desbloquea el panel de plataforma (/dashboard/admin/*
            // + AdminHomePanel). Las páginas admin van a /admin/* (requirePlatform), no usan businessId.
            const adminUser: User = {
                uid: perms.admin ? String(perms.admin.id) : 'platform',
                email: perms.admin?.email ?? null,
                displayName: perms.admin?.name ?? 'Administrador',
                businessName: 'Plataforma Lealify',
                businessId: null,
                branches: [],
                role: 'admin',
                subscription: { plan: 'pro', startDate: null, endDate: null, status: 'active' },
                hasCompletedOnboarding: true,
                timeZone: 'America/Mexico_City',
                maxBranches: 1,
                whatsappEnabled: false,
            };
            tagAnalyticsUser(null, 'platform');
            setAuthState({ user: adminUser, loading: false, error: null, needsOnboarding: false, tours: null });
            return;
        }
        if (!perms.user) {
            setAuthState({ user: null, loading: false, error: null, needsOnboarding: false, tours: null });
            return;
        }
        const [tenant, loyalty, branchesResp, account, waStatus] = await Promise.all([
            baseAuthApi.tenant(),
            baseAuthApi.loyaltySettings(),
            baseAuthApi.allBranches()   // incluye inactivas: "Mi Negocio" debe poder reactivarlas; el resto de consumidores filtra is_active
                // 🚨 10-oct-2026: /biz/branches exige settings:view y un EMPLEADO no lo tiene → 403 → el Promise.all entero
                // reventaba y el login pintaba «El sistema no responde» (Peak 7-oct, Bramarket). Respaldo: sus sucursales.
                .catch(() => baseAuthApi.branches()),
            // Suscripción REAL (Inc13 F13.6). Si falla, buildUser cae al sintético de loyalty.isPro.
            billingApi.getAccount().catch(() => null),
            // WhatsApp aprovisionado para el negocio (configured = sesión WAHA lista). Si falla → false.
            whatsappApi.status().catch(() => null),
        ]);
        let registeredClients = 0;
        try { registeredClients = (await baseAuthApi.contactsCount()).total || 0; } catch { /* no bloquea */ }
        const user = buildUser(perms, tenant, loyalty, branchesResp.branches || [], registeredClients, account, waStatus);
        tagAnalyticsUser(user.businessId || null, user.baseRole || 'desconocido');
        // needsOnboarding derivado del estado real: un negocio nuevo (onboarded=false) entra
        // al wizard; el resto no. PrivateRoute usa esto para redirigir a /onboarding.
        setAuthState({ user, loading: false, error: null, needsOnboarding: !user.hasCompletedOnboarding, tours: perms.tours ?? {} });
    }, []);

    // Restaurar sesión al montar (cookie). 401 = no hay sesión → estado anónimo.
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                await loadContext();
            } catch (e) {
                if (cancel) return;
                if (e instanceof BaseApiError && e.status === 401) {
                    setAuthState({ user: null, loading: false, error: null, needsOnboarding: false, tours: null });
                } else {
                    console.error('[AuthContext] restore error:', e);
                    setAuthState({ user: null, loading: false, error: null, needsOnboarding: false, tours: null });
                }
            }
        })();
        return () => { cancel = true; };
    }, [loadContext]);

    const clearError = useCallback(() => {
        setAuthState(prev => ({ ...prev, error: null }));
    }, []);

    const register = async (email: string, password: string, displayName: string, businessName: string, phoneNumber?: string, countryCode?: string, marketing?: { gaClientId?: string | null; gaSessionId?: string | null; utm?: Record<string, string> | null; landing?: Record<string, string> | null }): Promise<void> => {
        // Alta self-service (restaura el flujo Firebase sobre lealify-base): crea el negocio +
        // owner + abre sesión. Tras el alta arranca el wizard de onboarding (needsOnboarding).
        setAuthState(prev => ({ ...prev, loading: true, error: null }));
        try {
            // TZ real del navegador (backend la valida contra IANA; inválida/ausente → America/Mexico_City).
            let timezone: string | null = null;
            try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* default del backend */ }
            await baseAuthApi.signup({
                email, password, name: displayName, businessName,
                phone: phoneNumber, countryCode, timezone,
                gaClientId: marketing?.gaClientId ?? null, gaSessionId: marketing?.gaSessionId ?? null, utm: marketing?.utm ?? null, landing: marketing?.landing ?? null,
            });
            // GA4: conversión de registro (paridad con el flujo viejo).
            (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag?.('event', 'sign_up', {
                method: 'email',
                // Botón y página de lealify.com que trajeron el alta (dimensiones origen/pagina en GA4).
                origen: marketing?.landing?.origen || '(sin dato)',
                pagina: marketing?.landing?.pagina || '(sin dato)',
            });
            // Carga el contexto ya autenticado → el negocio nuevo cae al dashboard operando.
            // NOTA: no forzamos needsOnboarding=true. El wizard rebotaba en bucle porque
            // buildUser fija hasCompletedOnboarding=true para todos (OnboardingWizardPage lo
            // saca a /dashboard y PrivateRoute lo devuelve a /onboarding). "Se registra y opera".
            await loadContext();
        } catch (error) {
            const msg = error instanceof BaseApiError ? error.message : 'No se pudo completar el registro. Intenta de nuevo.';
            setAuthState(prev => ({ ...prev, loading: false, error: msg }));
            throw error;
        }
    };

    const login = async (email: string, password: string) => {
        setAuthState(prev => ({ ...prev, loading: true, error: null }));
        try {
            try {
                await baseAuthApi.login(email, password);
            } catch (opErr) {
                // Si no es operador de ningún negocio vivo (401), intentar como super_admin
                // de plataforma. loadContext() detecta la sesión de plataforma (perms.platform).
                if (opErr instanceof BaseApiError && opErr.status === 401) {
                    await baseAuthApi.platformLogin(email, password);
                } else {
                    throw opErr;
                }
            }
            await loadContext();
        } catch (error) {
            // Sin `status` = el fetch nunca obtuvo respuesta: es la red del aparato, no el
            // sistema. Decírselo evita el soporte a ciegas (caso Ale Huitrón, 4-sep-2026).
            const msg = error instanceof BaseApiError
                ? (error.status === 401
                    ? 'Email o contraseña incorrectos'
                    : 'El sistema no responde en este momento. Intenta de nuevo en un minuto.')
                : 'Sin conexión: el dispositivo no pudo comunicarse. Revisa el WiFi o los datos móviles e intenta otra vez.';
            setAuthState(prev => ({ ...prev, loading: false, error: msg }));
            throw error;
        }
    };

    const logout = async () => {
        try {
            await baseAuthApi.logout();
            setAuthState({ user: null, loading: false, error: null, needsOnboarding: false, tours: null });
        } catch (error) {
            console.error('[AuthContext] Logout error:', error);
            setAuthState({ user: null, loading: false, error: null, needsOnboarding: false, tours: null });
            throw error;
        }
    };

    // Actualiza la config del PROGRAMA (puntos/modo/capturas) en base. El branding completo
    // (nombre/colores del tenant) se cablea en Inc3 (Mi Negocio); aquí se refleja en local.
    const updateBusinessInfo = async (_businessId: string, data: Partial<Omit<User, 'uid' | 'email' | 'role' | 'subscription' | 'globalThemes'>>) => {
        if (!authState.user) { addError('No autenticado.', 'permission', 'updateBusinessInfo'); return; }
        try {
            const map: Record<string, unknown> = {};
            if ('loyaltyMode' in data) map.loyalty_mode = (data as any).loyaltyMode;
            if ('pointsPerVisit' in data) map.points_per_visit = (data as any).pointsPerVisit;
            if ('cashbackPercentage' in data) map.cashback_percentage = (data as any).cashbackPercentage;
            if ('cashbackSpecialTag' in data) map.cashback_special_tag = (data as any).cashbackSpecialTag || null;
            if ('cashbackSpecialPercentage' in data) map.cashback_special_percentage = (data as any).cashbackSpecialPercentage ?? null;
            if ('specialDays' in data) map.special_days = Array.isArray((data as any).specialDays) ? (data as any).specialDays : [];
            if ('timeBetweenVisits' in data) map.time_between_visits_min = (data as any).timeBetweenVisits;
            if ('allowPointsAccumulation' in data) map.allow_points_accumulation = (data as any).allowPointsAccumulation ? 1 : 0;
            if ('requestGender' in data) map.request_gender = (data as any).requestGender ? 1 : 0;
            if ('requestBirthday' in data) map.request_birthday = (data as any).requestBirthday ? 1 : 0;
            if ('requestEmail' in data) map.request_email = (data as any).requestEmail ? 1 : 0;
            if ('birthdayPointsMode' in data) map.birthday_points_mode = (data as any).birthdayPointsMode;
            if ('referralsEnabled' in data) map.referrals_enabled = (data as any).referralsEnabled ? 1 : 0;
            if ('pointsPerReferral' in data) map.points_per_referral = (data as any).pointsPerReferral;
            if ('referralBonusForNewClient' in data) map.referral_bonus_new_client = (data as any).referralBonusForNewClient;
            if (Object.keys(map).length) await baseApi.put('/loyalty/settings', map);

            // Branding del negocio (nombre/colores/logo/zona horaria) → /biz/settings.
            const brand: Record<string, unknown> = {};
            if ('businessName' in data) brand.name = (data as any).businessName;
            if ('businessColors' in data && (data as any).businessColors) {
                brand.primary_color = (data as any).businessColors.primary;
                brand.secondary_color = (data as any).businessColors.secondary;
            }
            if ('logoUrl' in data) brand.logo_url = (data as any).logoUrl;
            if ('timeZone' in data) brand.timezone = (data as any).timeZone;
            if ('phoneNumber' in data) brand.phone = (data as any).phoneNumber;   // teléfono del dueño → users.phone
            if (Object.keys(brand).length) await baseApi.put('/biz/settings', brand);

            // Sucursales: el form mantiene un array local y guarda al pulsar "Guardar". Reconciliamos
            // contra POST/PATCH /biz/branches. Las nuevas tienen id uuid (las del server son numéricas).
            if ('branches' in data && Array.isArray((data as any).branches)) {
                const incoming = (data as any).branches as Array<{ id: string; name: string; isActive?: boolean; cashbackPercentage?: number | null }>;
                const prevById = new Map((authState.user?.branches || []).map(b => [String(b.id), b]));
                let branchesChanged = false;
                for (const b of incoming) {
                    const active = b.isActive !== false;
                    if (!/^\d+$/.test(String(b.id))) {
                        // id uuid → sucursal nueva (el POST la crea activa por defecto)
                        await baseApi.post('/biz/branches', { name: b.name, cashback_percentage: b.cashbackPercentage ?? null });
                        branchesChanged = true;
                    } else {
                        const prev = prevById.get(String(b.id));
                        const nameChanged = !prev || prev.name !== b.name;
                        const activeChanged = !prev || (prev.isActive !== false) !== active;
                        const pctChanged = !prev || (prev.cashbackPercentage ?? null) !== (b.cashbackPercentage ?? null);
                        if (nameChanged || activeChanged || pctChanged) {
                            await baseApi.patch(`/biz/branches/${b.id}`, { name: b.name, is_active: active ? 1 : 0, cashback_percentage: b.cashbackPercentage ?? null });
                            branchesChanged = true;
                        }
                    }
                }
                if (branchesChanged) {
                    // Recargar TODAS (incl. inactivas) para tener los ids reales de las nuevas.
                    const fresh = await baseAuthApi.allBranches();
                    const mapped: Branch[] = (fresh.branches || []).map((b) => ({
                        id: String(b.id), name: b.name, isActive: b.is_active !== 0,
                        cashbackPercentage: b.cashback_percentage != null ? Number(b.cashback_percentage) : null,
                    } as Branch));
                    setAuthState(prev => ({ ...prev, user: prev.user ? ({ ...prev.user, ...data, branches: mapped } as User) : null }));
                    return;
                }
            }

            setAuthState(prev => ({ ...prev, user: prev.user ? { ...prev.user, ...data } : null }));
        } catch (error) {
            console.error('[AuthContext] Error updating business info:', error);
            addError('Error al actualizar la información', 'unknown', 'updateBusinessInfo');
            throw error;
        }
    };

    const updateBusinessTheme = async () => Promise.resolve();
    const updateGlobalThemes = async () => Promise.resolve();

    const markOnboardingComplete = async (): Promise<void> => {
        // Persiste en backend (tenants.onboarded=1) para que NO se repita en el próximo login.
        // Best-effort: aunque falle la red, no re-atrapamos al usuario en el wizard localmente.
        try { await baseAuthApi.markOnboarding(); } catch (e) { console.error('[AuthContext] markOnboarding:', e); }
        setAuthState(prev => ({ ...prev, needsOnboarding: false, user: prev.user ? { ...prev.user, hasCompletedOnboarding: true } : null }));
    };

    const markInstructionsDismissed = async (): Promise<void> => {
        if (!authState.user?.businessId) return;
        const t = authState.user.businessId;
        const prefs = readPrefs(t); prefs.instructionsDismissed = true; writePrefs(t, prefs);
        setAuthState(prev => ({ ...prev, user: prev.user ? { ...prev.user, instructionsDismissed: true } : null }));
    };

    const markHowtoSeen = async (sectionKey: string): Promise<void> => {
        if (!authState.user?.businessId) return;
        const t = authState.user.businessId;
        const prefs = readPrefs(t); prefs.howtosSeen = { ...(prefs.howtosSeen || {}), [sectionKey]: true }; writePrefs(t, prefs);
        setAuthState(prev => ({
            ...prev,
            user: prev.user ? { ...prev.user, howtosSeen: { ...(prev.user.howtosSeen || {}), [sectionKey]: true } } : null,
        }));
    };

    const updateCustomMessage = async (messageType: string, message: string): Promise<void> => {
        // Inc11 (mensajes automáticos): se cableará a /api/messages/settings. Por ahora, local.
        setAuthState(prev => {
            if (!prev.user) return prev;
            const updated = { ...prev.user.customMessages, [messageType]: message };
            return { ...prev, user: { ...prev.user, customMessages: updated } };
        });
    };

    const patchCurrentUser = (data: Partial<User>) =>
        setAuthState(prev => ({ ...prev, user: prev.user ? ({ ...prev.user, ...data } as User) : null }));

    const value: AuthContextType = {
        currentUser: authState.user,
        loading: authState.loading,
        error: authState.error,
        needsOnboarding: authState.needsOnboarding,
        register,
        login,
        logout,
        clearError,
        updateBusinessInfo,
        updateBusinessTheme,
        updateGlobalThemes,
        markOnboardingComplete,
        markInstructionsDismissed,
        markHowtoSeen,
        registeredClients: authState.user?.registeredClients || 0,
        instructionsDismissed: authState.user?.instructionsDismissed || false,
        updateCustomMessage,
        tours: authState.tours,
        patchCurrentUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// `checkIsPro` se mantiene importado: los consumidores leen subscription que sigue
// cumpliendo el contrato { plan, status } que ese helper espera.
void checkIsPro;
